import { Sparkline } from '../../charts'
import { trendValues } from '../ceoCockpitModel'
import { formatCount, formatCurrencyExact, formatPctPoints, formatShare } from '../ceoFormat'
import { isHiddenAvailability } from '../ceoCockpitTypes'
import type { CeoCockpitResponse, CeoPeriodKind } from '../ceoCockpitTypes'
import { CeoNotTrackedState } from './CeoNotTrackedState'

const NOT_PREPARED = 'Not prepared'

export function CeoLaborPanel({
  payload,
  period = 'mtd',
  onAsk,
}: {
  payload: CeoCockpitResponse
  period?: CeoPeriodKind
  onAsk?: (prompt: string) => void
}) {
  const labor = payload.labor
  if (!labor) return null
  if (labor.availability?.status === 'not_tracked') {
    return (
      <section className="ceo-section rise" id="ceo-labor" data-testid="ceo-labor">
        <p className="ceo-kicker">Labor</p>
        <CeoNotTrackedState connectTarget={labor.availability?.connect_target || 'payroll'} onAsk={onAsk} />
      </section>
    )
  }
  const headcountRows = Array.isArray(labor.headcount_by_department) ? labor.headcount_by_department : []
  const pctRows = Array.isArray(labor.labor_pct_of_sales_by_location) ? labor.labor_pct_of_sales_by_location : []
  const utilization = labor.utilization?.overall
  const utilizationLabel = utilization == null || !Number.isFinite(utilization) ? NOT_PREPARED : formatShare(utilization)
  // Not-applicable sections render nothing at all: a restaurant has no billable
  // utilization to be missing, so offering an empty tile is worse than silence.
  const showUtilization = !isHiddenAvailability(labor.utilization_availability)
  const showLaborPct = !isHiddenAvailability(labor.labor_pct_availability)
  return (
    <section className="ceo-section rise" id="ceo-labor" data-testid="ceo-labor">
      <p className="ceo-kicker">Labor</p>
      <div className="ceo-saas-tiles">
        <article className="ceo-mini-tile"><span className="ceo-kicker">Headcount</span><b>{formatCount(labor.headcount)}</b></article>
        <article className={`ceo-mini-tile${labor.revenue_per_employee == null ? ' is-empty' : ''}`}>
          <span className="ceo-kicker">Rev / employee</span>
          <b>{labor.revenue_per_employee == null || !Number.isFinite(labor.revenue_per_employee) ? NOT_PREPARED : formatCurrencyExact(labor.revenue_per_employee)}</b>
        </article>
        {showUtilization ? (
          <article className={`ceo-mini-tile${utilizationLabel === NOT_PREPARED ? ' is-empty' : ''}`}>
            <span className="ceo-kicker">Utilization</span>
            <b>{utilizationLabel}</b>
          </article>
        ) : null}
      </div>
      <div className="ceo-inv-cols">
        <div>
          <p className="ceo-kicker">Headcount by department · 13 months</p>
          {headcountRows.length ? (
            <ul className="ceo-plain-list ceo-labor-headcount">
              {headcountRows.map((row) => {
                const series = trendValues(row.series_13m)
                return (
                  <li key={row.key}>
                    <b>{row.label}</b>
                    <span className="ceo-labor-headcount-value">
                      {series.length >= 2 ? (
                        <Sparkline values={series} width={72} height={18} ariaLabel={`${row.label} headcount, 13 months`} />
                      ) : null}
                      <span>{formatCount(row.count)}</span>
                    </span>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="ceo-muted">Not prepared — no employee entities on payroll lines yet.</p>
          )}
        </div>
        {showLaborPct ? (
          <div>
            <p className="ceo-kicker">Labor % of sales vs target · last 12 months</p>
            {pctRows.length ? (
              <ul className="ceo-plain-list">
                {pctRows.map((row) => {
                  // Labor share is a TRAILING ratio, never month-to-date. Pay
                  // periods and billing cycles do not line up inside a partial
                  // month: on the 17th, Fenwick has booked a fortnight of
                  // salaries against almost no revenue (it bills at month end)
                  // and reads 306% of sales; Cedar reads 45% against a 30%
                  // target for the same reason. The twelve-month ratio is the
                  // number an operator actually manages to.
                  const value = row.ttm ?? row.ytd ?? row.qtd ?? row[period] ?? row.mtd
                  const prepared = value != null && Number.isFinite(value)
                  const target = row.target
                  const gap = prepared && target != null && Number.isFinite(target) ? value - target : null
                  return (
                    <li key={row.key}>
                      <b>{row.label}</b>
                      <span>
                        {prepared ? formatShare(value) : NOT_PREPARED}
                        {target != null && Number.isFinite(target) ? ` vs ${formatShare(target)} target` : ''}
                        {gap != null && Math.abs(gap) >= 0.0005 ? (
                          <small className={`ceo-labor-gap ${gap > 0 ? 'is-over' : 'is-under'}`}> {formatPctPoints(gap)}</small>
                        ) : null}
                      </span>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="ceo-muted">Payroll is not yet coded to a location.</p>
            )}
          </div>
        ) : null}
      </div>
    </section>
  )
}
